/** Calendar arithmetic is duplicated across builds and pinned by recurrence-calendar.test.ts. */
export type Repeat = "daily" | "weekly" | "monthly" | "yearly";
export interface OccurrenceDue { date: string; time: string | null; tz: string | null }
export interface Completion {
  id: string;
  due: OccurrenceDue;
  lastDoneOn: string | null;
  /** The completion time this completion replaced (g105), kept by the server so its undo can put it back. A token made on a device has none. */
  prior?: { completedAt: number | null; completedAtSource: "device" | "server" | null; completionEventId: string | null };
}
export interface Recurrence {
  repeat?: Repeat | null;
  lastDoneOn?: string | null;
  repeatAnchor?: string | null;
  occurrenceRevision?: number;
  completion?: Completion | null;
}

const DAY = 86_400_000;
function calendar(date: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error("Invalid calendar date.");
  const value = new Date(date + "T00:00:00Z");
  if (!Number.isFinite(value.getTime()) || value.toISOString().slice(0, 10) !== date ||
      date < "1900-01-01" || date > "2999-12-31") throw new Error("Date must be between 1900 and 2999.");
  return value;
}
function iso(date: Date): string {
  const result = date.toISOString().slice(0, 10);
  calendar(result);
  return result;
}
function clamped(year: number, month: number, day: number): Date {
  const end = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, end)));
}

/** First scheduled occurrence strictly after the completion day, retaining the original anchor. */
export function roll(due: string, repeat: Repeat, today: string, anchor: string): string {
  const current = calendar(due);
  const completed = calendar(today);
  const original = calendar(anchor);
  if (due > today) throw new Error("This occurrence is not due yet.");
  if (repeat === "daily" || repeat === "weekly") {
    const step = repeat === "daily" ? 1 : 7;
    const days = Math.floor((completed.getTime() - current.getTime()) / DAY);
    return iso(new Date(current.getTime() + (Math.floor(days / step) + 1) * step * DAY));
  }
  let year = completed.getUTCFullYear();
  let month = repeat === "monthly" ? completed.getUTCMonth() : original.getUTCMonth();
  let next = clamped(year, month, original.getUTCDate());
  if (next <= completed) {
    if (repeat === "monthly") month += 1;
    else year += 1;
    next = clamped(year, month, original.getUTCDate());
  }
  return iso(next);
}

/** Offline timestamps do not expire. A timestamp is a client claim, not proof of wall-clock time. */
export function completionDay(at: number, timeZone: string, now = Date.now()): string {
  if (!Number.isSafeInteger(at) || at > now + 300_000) throw new Error("Check the device clock and try again.");
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(at));
  const part = (kind: string) => parts.find(value => value.type === kind)?.value;
  const result = part("year") + "-" + part("month") + "-" + part("day");
  calendar(result);
  return result;
}
